import React, { useEffect, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';

import { useAppleStatusQuery } from 'apollo/queries/autogenerate/hooks';
import { useConsumerStatusSummaryQuery } from 'apollo/queries/autogenerate/hooks';
import { BillStatement } from 'apollo/queries/autogenerate/schemas';
import { KYCStatus } from 'apollo/queries/autogenerate/schemas';
import {
  EKYC_APPLICATION_STAGE,
  EKYC_APPLICATION_STATUS,
} from 'shared/constants/ekyc';
import { isPlusUser } from 'shared/consumer/utils/consumer-types';
import HighlightedBills from 'shared/modules/bill/components/organisms/HighlightedBills';
import isHighPriceBill from 'shared/modules/bill/utils/is-high-price-bill';
import Button from 'shared/modules/common/components/atoms/Button';
import Dialog from 'shared/modules/common/components/atoms/Dialog';
import Banners from 'shared/modules/common/components/organisms/Banners';
import Help from 'shared/modules/common/components/organisms/Help';
import HelpTiles from 'shared/modules/common/components/organisms/HelpTiles';
import useIsShoppingEnabled from 'shared/modules/common/hooks/use-is-shopping-enabled';
import useIsWebEKYCEnabled from 'shared/modules/common/hooks/use-is-web-ekyc-enabled';
import KYCAdvertisementBanner from 'shared/modules/ekyc/components/organism/Banner';
import {
  SCREEN as EKYC_MIXPANEL_SCREEN,
  ITEM as EKYC_MIXPANEL_ITEM,
} from 'shared/modules/ekyc/constants/mixpanel';
import { mixpanelTrackClickEvent } from 'shared/modules/ekyc/utils/kyc-tracking-events';
import {
  ACTION,
  ITEM,
  SCREEN,
} from 'shared/modules/settlement/constants/mixpanel';
import { createCookie, getCookie } from 'shared/utils/cookie';
import eventBus from 'shared/utils/event-bus';
import Mixpanel from 'shared/vendor/Mixpanel';
import Footer from 'web/modules/common/components/organisms/Footer';
import routes from 'web/navigation/routes';
import EKYCAbandonedJourneyDialog from 'web/screens/Authenticated/Account/EKYCAbandonedJourneyDialog';
const EKYC_ONBOARDING_COOKIE = 'isEKYCOnboardingDisplayed';

const HomeScreen = () => {
  const [isHelpShown, setIsHelpShown] = useState(false);
  const [helpIndex, setHelpIndex] = useState(-1);
  const isShoppingEnabled = useIsShoppingEnabled();
  const isBigScreen = useMediaQuery({ minWidth: 768 });
  const navigate = useNavigate();
  const { data: appleData } = useAppleStatusQuery({
    fetchPolicy: 'cache-only',
  });
  const { data } = useConsumerStatusSummaryQuery();
  const kycStatus = data?.KYCApplicationStatus?.status;
  const houkatsuStatus = data?.houkatsuStatus.status;

  const isWebKYCEnabled = useIsWebEKYCEnabled();

  const [
    isEKYCAbandonedJourneyDialogOpen,
    setIsEKYCAbandonedJourneyDialogOpen,
  ] = useState<boolean>(false);

  useEffect(() => {
    triggerEKYCOnboarding();

    // Check if there's a pending EKYC application
    if (getCookie(EKYC_APPLICATION_STATUS)) {
      setIsEKYCAbandonedJourneyDialogOpen(true);
    }
  }, []);

  const navigateToBillDetails = (bill: BillStatement) => {
    if (!bill.autoDebitSettlement && isHighPriceBill(bill)) {
      navigate(
        routes.authenticated.home.billSettlementBankTransfer.replace(
          ':id',
          bill.id
        )
      );

      return;
    }

    navigate(routes.authenticated.home.billDetails.replace(':id', bill.id));
  };

  const openWebpage = (url: string) => window.open(url, '_blank');

  const getItemNameFromBannerId = (id: string) => {
    switch (id) {
      case 'apple':
        return ITEM.APPLE_BANNER;
      case 'shopping':
        return ITEM.SHOPPING_BANNER;
      case 'amazon':
        return ITEM.AMAZON_BANNER;
      default:
        return '';
    }
  };

  const getItemNameFromHowToIndex = (index: number) => {
    switch (index) {
      case 0:
        return ITEM.PAY_LATER;
      case 1:
        return ITEM.SETTLEMENT_METHODS;
      case 2:
        return ITEM.N_PAY;
      case 3:
        return ITEM.SHOPPING;
      default:
        return '';
    }
  };

  const trackMixpanelBannerEvent = (id: string) => {
    const itemName = getItemNameFromBannerId(id);

    Mixpanel.trackEvent(`${SCREEN.HOME_SCREEN} - ${ACTION.CLICK}`, {
      Item: itemName,
    });
  };

  const trackMixpanelHowToEvent = (index: number) => {
    const itemName = getItemNameFromHowToIndex(index);

    Mixpanel.trackEvent(`${SCREEN.HOME_SCREEN} - ${ACTION.CLICK}`, {
      Item: itemName,
    });
  };

  const triggerEKYCOnboarding = () => {
    if (
      isWebKYCEnabled &&
      !getCookie(EKYC_ONBOARDING_COOKIE) &&
      (kycStatus === KYCStatus.NEVER_APPLIED ||
        kycStatus === KYCStatus.REJECTED)
    ) {
      const eventName = `${SCREEN.HOME_SCREEN} - Onboarding`;

      Mixpanel.trackEvent(eventName, {
        Item: 'Start eKYC Onboarding',
      });

      eventBus.emit('ekyc-onboarding:open');
      createCookie(EKYC_ONBOARDING_COOKIE, 'true', 360);
    }
  };

  const onResumeEKYCApplication = () => {
    let targetRoute = null;

    if (
      getCookie(EKYC_APPLICATION_STATUS) ===
      EKYC_APPLICATION_STAGE.PRE_APPLICATION
    ) {
      targetRoute = '/ekyc';
    } else if (
      getCookie(EKYC_APPLICATION_STATUS) ===
      EKYC_APPLICATION_STAGE.POST_APPLICATION
    ) {
      targetRoute = '/ekyc/apply';
    }

    if (targetRoute) {
      mixpanelTrackClickEvent(EKYC_MIXPANEL_SCREEN.WEB_CONTINUE_PAIDY_FORM, {
        Item: EKYC_MIXPANEL_ITEM.WEB_EKYC_RETRY_AFTER_DROP_OFF,
      });

      const newTab = window.open(
        process.env.MY_PAIDY_URL + targetRoute,
        '_blank'
      );

      newTab?.location;
    }
  };

  return (
    <View style={[style.screen, isBigScreen && style.screenBigScreen]}>
      <View style={[style.container, isBigScreen && style.containerBigScreen]}>
        <View>
          <Text style={style.sectionTitle}>ご利用状況</Text>
          <Button
            onPress={() => navigate(routes.authenticated.home.billSummary)}
            title="すべて見る"
            variant="outlined"
            style={style.seeAllButton}
            id="highlighted-bills-see-all-button"
          />
        </View>
        <View style={style.section}>
          <HighlightedBills
            onItemDetailPress={navigateToBillDetails}
            onItemPayButtonPress={navigateToBillDetails}
            onOverdueNotificationItemPress={navigateToBillDetails}
            onOverdueNotificationLinkPress={() =>
              openWebpage(process.env.INQUIRY_URL)
            }
            onPaymentMethodPress={(billId: string) => {
              navigate(
                `${routes.authenticated.account.root}?bill_id=${billId}`
              );
            }}
          />
        </View>
        {isWebKYCEnabled &&
          (kycStatus === KYCStatus.NEVER_APPLIED ||
            kycStatus === KYCStatus.REJECTED) && (
            <>
              <Text style={style.sectionTitle}>
                {`ペイディをもっと便利に利用する`}
              </Text>
              <View style={style.section}>
                <KYCAdvertisementBanner
                  onPress={() => {
                    const eventName = `${SCREEN.HOME_SCREEN} - ${ACTION.CLICK}`;

                    Mixpanel.trackEvent(eventName, {
                      Item: 'Start eKYC Onboarding',
                    });

                    eventBus.emit('ekyc-onboarding:open');
                  }}
                />
              </View>
            </>
          )}
        <Text style={style.sectionTitle}>お買い物を楽しむ</Text>
        <View style={style.section}>
          <Banners
            onItemPress={(item) => {
              trackMixpanelBannerEvent(item.id);

              if (
                item.id === 'apple' &&
                appleData?.appleCreditStatus.status === 'APPROVED'
              ) {
                openWebpage(
                  'https://www.apple.com/jp/shop/buy-iphone/iphone-15?cid=aos-jp-aff-bp-paidy-mypaidy-event'
                );
              } else {
                eventBus.emit('appdl:open');
              }
            }}
          />
        </View>
        <Text style={style.sectionTitle}>使い方を知る</Text>
        <HelpTiles
          onItemPress={(index) => {
            trackMixpanelHowToEvent(index);

            // PLUS USER
            if (
              isWebKYCEnabled &&
              index === 2 &&
              isPlusUser(kycStatus, houkatsuStatus)
            ) {
              const newTab = window.open('https://paidy.com/npay/', '_blank');

              newTab?.location;

              return;
            }

            setHelpIndex(index);
            setIsHelpShown(true);
          }}
          isShoppingEnabled={isShoppingEnabled}
        />
        <Dialog
          open={isHelpShown}
          title=""
          onBackdropPress={() => setIsHelpShown(false)}
          onCloseButtonPress={() => setIsHelpShown(false)}
          onClose={() => setHelpIndex(-1)}
          style={style.dialog}
          id="help-dialog"
        >
          <Help
            style={style.help}
            activeScreenIndex={helpIndex}
            onItemPress={(item) => {
              if (item === 'bank-transfer') {
                // Navigate to ADRS
                navigate(routes.authenticated.account.root);
              }

              if (item === 'faq') {
                const newTab = window.open(process.env.FAQ_URL, '_blank');

                newTab?.location;
              }

              if (item === 'shopping') {
                setIsHelpShown(false);
                // Close the Help dialog first before showing the AppDownload Dialog
                setTimeout(() => {
                  eventBus.emit('appdl:open');
                }, 250);
              }

              // Hoshino 6-pay rollout changes for 3/6Pay tile
              if (item === 'npay') {
                const newTab = window.open('https://paidy.com/npay/', '_blank');

                newTab?.location;
              }

              if (item === 'kyc') {
                setIsHelpShown(false);

                setTimeout(() => {
                  const eventName = `${SCREEN.HOW_TO} - ${ACTION.CLICK}`;

                  Mixpanel.trackEvent(eventName, {
                    Item: 'Start eKYC Onboarding',
                  });

                  eventBus.emit('ekyc-onboarding:open');
                }, 250);
              }
            }}
          />
        </Dialog>
        <Footer />
      </View>
      <EKYCAbandonedJourneyDialog
        open={isEKYCAbandonedJourneyDialogOpen}
        onClose={() => setIsEKYCAbandonedJourneyDialogOpen(false)}
        onNextButtonPress={onResumeEKYCApplication}
      />
    </View>
  );
};

const style = StyleSheet.create({
  // Template for building a new main screen
  // Scrolls on overflow for both mobile and desktop
  screen: {
    alignItems: 'center',
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    overflow: 'scroll',
    paddingVertical: 24,
    paddingHorizontal: 16,
  },
  screenBigScreen: {
    paddingHorizontal: 24,
  },
  container: {
    width: '100%',
  },
  containerBigScreen: {
    maxWidth: 560,
  },
  sectionTitle: {
    color: '#607D8B',
    fontWeight: 'bold',
    marginBottom: 16,
  },
  section: {
    marginBottom: 24,
  },
  seeAllButton: {
    height: 32,
    width: 88,
    paddingVertical: 8,
    paddingHorizontal: 8,
    position: 'absolute',
    top: -5,
    right: 0,
  },
  dialog: {
    width: '100%',
    height: '100%',
    maxWidth: 375,
  },
  help: {
    margin: -24,
    marginTop: -88,
    borderRadius: 8,
  },
});

export default HomeScreen;
